import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import { InitialStateMinimumRequirements_GraphQl } from "../../graphql/initial-state-requirements";

import { AddProductsToWishlistOutput } from "../../model";

import {
  BbntWishlistItemInput,
  gqlAddProductsToWishlist,
  GraphqlResponseAddProductsToWishlist,
} from "./data-mutation-add-to-wishlist-provider-gql-query-factory";
import { DataMutationAddToWishlistProvider_QueryKey } from "./data-mutation-add-to-wishlist-provider-query-key-factory";

export interface DataMutationAddToWishlistConsumerInterface {
  data: DataMutationAddToWishlist_Result["data"];
  request: DataMutationAddToWishlist_Result["variables"];
  mutateAsync: DataMutationAddToWishlist_Result["mutateAsync"];
  status: DataMutationAddToWishlist_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationAddToWishlistConsumerInterface;
const DataMutationAddToWishlistContext = React.createContext(defaultValue);

export interface DataMutationAddToWishlist_Request {
  addTheseItemsToWishlist: BbntWishlistItemInput[];
}
export type DataMutationAddToWishlist_Result = ReactQuery.UseMutationResult<
  AddProductsToWishlistOutput,
  unknown,
  DataMutationAddToWishlist_Request,
  unknown
>;

export interface DataMutationAddToWishlistProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationAddToWishlistProvider: React.FunctionComponent<
  DataMutationAddToWishlistProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutationAddToWishlistProvider_QueryKey.addToWishlist();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    AddProductsToWishlistOutput,
    DataMutationAddToWishlist_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = gqlAddProductsToWishlist(
        request.addTheseItemsToWishlist
      );

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponseAddProductsToWishlist>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      const { data: gqlResponseData } = data || {};
      const { addProductsToWishlist } = gqlResponseData || {};

      return addProductsToWishlist;
    },
    [initialState]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {});

  const value: DataMutationAddToWishlistConsumerInterface = React.useMemo(
    () => ({
      data: mutation.data,
      request: mutation.variables,
      mutateAsync: mutation.mutateAsync,
      status: mutation.status,
    }),
    [mutation, mutation.mutateAsync]
  );
  return (
    <DataMutationAddToWishlistContext.Provider value={value}>
      {props.children || []}
    </DataMutationAddToWishlistContext.Provider>
  );
};

export const useDataMutationAddToWishlist = () => {
  const context = React.useContext(DataMutationAddToWishlistContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationAddToWishlist must be used within a DataMutationAddToWishlistProvider"
    );
  }
  return context;
};
